import { useState, useEffect } from 'react'
import { ChevronDown, Building2, Check, Plus } from 'lucide-react'
import { Workspace } from '../types/workspace'
import { getApiBase, getToken } from '../lib/utils'
import CreateWorkspaceModal from './CreateWorkspaceModal'

interface WorkspaceSwitcherProps {
  currentWorkspaceId?: number
  onWorkspaceChange?: (workspaceId: number) => void
}

export default function WorkspaceSwitcher({ currentWorkspaceId, onWorkspaceChange }: WorkspaceSwitcherProps) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    fetchWorkspaces()
  }, [])

  useEffect(() => {
    if (workspaces.length > 0 && currentWorkspaceId) {
      const workspace = workspaces.find(w => w.id === currentWorkspaceId)
      if (workspace) {
        setCurrentWorkspace(workspace)
      }
    } else if (workspaces.length > 0 && !currentWorkspaceId) {
      // Par défaut, sélectionner le premier workspace (owner)
      const defaultWorkspace = workspaces[0]
      setCurrentWorkspace(defaultWorkspace)
      
      // Notifier le parent et stocker dans localStorage
      localStorage.setItem('current_workspace_id', defaultWorkspace.id.toString())
      if (onWorkspaceChange) {
        onWorkspaceChange(defaultWorkspace.id)
      }
    }
  }, [workspaces, currentWorkspaceId, onWorkspaceChange])

  const fetchWorkspaces = async () => {
    try {
      const token = getToken()
      if (!token) return

      const response = await fetch(`${getApiBase()}/workspaces?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setWorkspaces(data.items || [])
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleWorkspaceSelect = (workspace: Workspace) => {
    setCurrentWorkspace(workspace)
    setIsOpen(false)
    
    // Stocker le workspace actuel dans localStorage
    localStorage.setItem('current_workspace_id', workspace.id.toString())
    
    // Callback pour notifier le parent
    if (onWorkspaceChange) {
      onWorkspaceChange(workspace.id)
    }
    
    // Recharger la page pour appliquer le contexte du nouveau workspace
    window.location.reload()
  }

  if (loading || workspaces.length === 0) {
    return null
  }

  // Si un seul workspace (Free/Pro), ne pas afficher le switcher
  if (workspaces.length === 1) {
    return null
  }

  return (
    <div className="relative">
      {/* Bouton Workspace Switcher */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-emerald-500/50 hover:bg-zinc-900 transition-all duration-200 group"
      >
        <Building2 className="h-4 w-4 text-zinc-400 group-hover:text-emerald-400 transition-colors" />
        <div className="flex flex-col items-start min-w-0">
          <span className="text-xs text-zinc-500 font-medium">Workspace</span>
          <span className="text-sm text-zinc-200 font-semibold truncate max-w-[150px]">
            {currentWorkspace?.name || 'Sélectionner'}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl z-50 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-800">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Vos workspaces
              </p>
            </div>

            {/* Workspaces List */}
            <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => handleWorkspaceSelect(workspace)}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/50 transition-colors ${
                    currentWorkspace?.id === workspace.id ? 'bg-emerald-500/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                      currentWorkspace?.id === workspace.id
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}>
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className={`text-sm font-medium truncate max-w-full ${
                        currentWorkspace?.id === workspace.id ? 'text-emerald-400' : 'text-zinc-200'
                      }`}>
                        {workspace.name}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {workspace.deployment_mode === 'production' ? 'Production' : 'Preview'}
                      </span>
                    </div>
                  </div>
                  {currentWorkspace?.id === workspace.id && (
                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>

            {/* Footer - Create New Workspace (Phase 3.2) */}
            <div className="px-4 py-3 border-t border-zinc-800">
              <button
                onClick={() => {
                  setIsOpen(false)
                  setShowCreateModal(true)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 hover:border-emerald-500/30 transition-all duration-200 text-emerald-400 text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Nouveau workspace
              </button>
            </div>
          </div>
        </>
      )}

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          fetchWorkspaces()
        }}
      />
    </div>
  )
}
